import styled from '@emotion/styled';
import { TextField } from '@material-ui/core';
import { Paperclip, Send, X } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import type { CopilotAttachment } from './useCopilotChat';

// Attachment limits — mirror the backend contract (chatTypes.ts). Text (CSV) ≤1MB;
// PDF ≤5MB (rides as base64 bytes, extracted to text server-side).
const MAX_TEXT_BYTES = 1_000_000;
const MAX_PDF_BYTES = 5_000_000;
const ACCEPT = '.csv,.tsv,.txt,.pdf,text/csv,text/tab-separated-values,text/plain,application/pdf';
const ALLOWED_MIME = ['text/csv', 'text/tab-separated-values', 'text/plain', 'application/pdf'];

/** Normalize to a backend-allowed mimeType (browsers often report '' for .csv). */
const normalizeMime = (file: File): string | null => {
  if (ALLOWED_MIME.includes(file.type)) return file.type;
  const ext = file.name.toLowerCase().split('.').pop();
  if (ext === 'csv') return 'text/csv';
  if (ext === 'tsv') return 'text/tab-separated-values';
  if (ext === 'txt') return 'text/plain';
  if (ext === 'pdf') return 'application/pdf';
  return null;
};

/** Read a file as base64 (no data: prefix) — robust for binary/large files via FileReader. */
const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result);
      resolve(s.slice(s.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const Footer = styled('div')`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top: 1px solid #ebedef;
  padding: 10px 12px 8px;
`;

const InputRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// "Dado Assistant is AI. It can make mistakes, so double check it." —
// subtle disclaimer under the input.
const Disclaimer = styled('div')`
  margin-top: 6px;
  text-align: center;
  font-size: 11px;
  font-weight: 300;
  color: #b0b8c1;
`;

const InputWrap = styled('div')`
  flex: 1;
  min-height: 36px;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px #dde0e3;
  padding: 0 12px;
  display: flex;
  align-items: center;
  transition: box-shadow 120ms ease;

  &:focus-within {
    box-shadow:
      inset 0 0 0 1px #0e78e4,
      0 0 0 3px rgba(14, 120, 228, 0.18);
  }
`;

const IconButton = styled('button')<{ disabled?: boolean }>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  color: #6b7280;
  transition: background 120ms ease, color 120ms ease;

  &:hover:not(:disabled) {
    background: #f0f2f5;
    color: #0e78e4;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FileChipRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const FileChip = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  background: #eef4fd;
  color: #0e78e4;
  border: 1px solid #cfe0fb;
  border-radius: 6px;
  padding: 3px 6px 3px 8px;
  font-size: 12px;
  font-weight: 400;
`;

const FileName = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 220px;
`;

const ChipRemove = styled('button')`
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  padding: 0;
`;

const FileError = styled('div')`
  color: #d64545;
  font-size: 11px;
  margin-bottom: 6px;
`;

const SendButton = styled('button')<{ disabled?: boolean }>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: ${(p) => p.theme.dado.copilot.gradientDiagonal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  color: #fff;
  box-shadow: ${(p) => p.theme.dado.copilot.sendButtonShadow};
  transition: filter 120ms ease, opacity 120ms ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: none;
    box-shadow:
      ${(p) => p.theme.dado.copilot.sendButtonShadow},
      0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

interface ChatInputProps {
  onSend: (message: string, attachments?: CopilotAttachment[]) => void;
  disabled?: boolean;
}

export const ChatInput: React.FC<React.PropsWithChildren<ChatInputProps>> = ({
  onSend,
  disabled,
}) => {
  const [value, setValue] = useState('');
  const [pendingFile, setPendingFile] = useState<CopilotAttachment | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-selecting the same file fires onChange again.
    e.target.value = '';
    if (!file) return;
    setFileError(null);
    const mimeType = normalizeMime(file);
    if (!mimeType) {
      setFileError('Please attach a CSV or PDF file.');
      return;
    }
    const isPdf = mimeType === 'application/pdf';
    const maxBytes = isPdf ? MAX_PDF_BYTES : MAX_TEXT_BYTES;
    if (file.size > maxBytes) {
      setFileError(`That file is larger than ${maxBytes / 1_000_000}MB.`);
      return;
    }
    try {
      // PDF → base64 bytes (extracted server-side); text/CSV → utf8 text.
      const content = isPdf ? await readAsBase64(file) : await file.text();
      setPendingFile({ name: file.name, mimeType, content, encoding: isPdf ? 'base64' : 'utf8' });
    } catch {
      setFileError('Could not read that file.');
    }
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if ((!trimmed && !pendingFile) || disabled) return;
    // File-only sends still need a message (the backend requires one); supply a
    // sensible default so the transcript reads naturally.
    const message = trimmed || (pendingFile ? `Please use the attached file "${pendingFile.name}" for my request.` : '');
    onSend(message, pendingFile ? [pendingFile] : undefined);
    setValue('');
    setPendingFile(null);
    setFileError(null);
  }, [value, pendingFile, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <Footer>
      {fileError && <FileError>{fileError}</FileError>}
      {pendingFile && (
        <FileChipRow>
          <FileChip>
            <Paperclip size={12} />
            <FileName title={pendingFile.name}>{pendingFile.name}</FileName>
            <ChipRemove type="button" onClick={() => setPendingFile(null)} aria-label="Remove attachment">
              <X size={12} />
            </ChipRemove>
          </FileChip>
        </FileChipRow>
      )}
      <InputRow>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <IconButton
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Attach a CSV or PDF file"
          title="Attach a CSV or PDF file"
        >
          <Paperclip size={16} />
        </IconButton>
        <InputWrap>
          <TextField
            fullWidth
            // Force standard variant: the app theme defaults TextField to
            // `outlined`, whose notched <fieldset> border rendered INSIDE
            // the InputWrap border (rectangle-in-a-rectangle). Standard +
            // disableUnderline = borderless; InputWrap is the only border.
            variant="standard"
            placeholder="Ask Assistant..."
            multiline
            maxRows={3}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            InputProps={{
              disableUnderline: true,
              style: { fontSize: 13, fontWeight: 300, lineHeight: '1.5' },
            }}
          />
        </InputWrap>
        <SendButton
          type="button"
          onClick={handleSend}
          disabled={disabled || (!value.trim() && !pendingFile)}
          aria-label="Send message"
        >
          <Send size={14} />
        </SendButton>
      </InputRow>
      <Disclaimer>Dado Assistant is AI. It can make mistakes, so double check it.</Disclaimer>
    </Footer>
  );
};
